import { FUTURES } from "../../constants";
import axios from "@helpers/axios/public.axios";
import store from "../../redux/store";
import {
  BINANCE_STREAM_HOST,
  subscribeBinanceStream,
} from "@helpers/binance/binanceStreams";

const ORDER_BOOK_UPDATE_DELAY = 500;
// Collect ticker updates of the pairs list and repaint it a few times per second, not per message
const TICKERS_FLUSH_DELAY_MS = 250;

const toTickerData = ({ c, P, h, l, v, q }) => ({
  lastPrice: c,
  priceChangePercent: P,
  highPrice: h,
  lowPrice: l,
  volume: v,
  quoteVolume: q,
});

export const subscribeToFutureSockets = (limit = 20) => (dispatch, getState) => {
  const depthStream = limit <= 10 ? "depth10@500ms" : "depth20@500ms";
  // Pair bound to this subscription: late events of the previous pair must not be tagged as the new one
  const socketPair = getState().futures.pair.slug.toLowerCase();
  let latestDepth = null;
  let depthFlushTimeout = null;
  let isClosed = false;

  const dispatchOrderBook = (snapshot, source) => {
    dispatch({
      type: FUTURES.UPDATE_ORDER_BOOK,
      payload: snapshot,
      meta: { source, pair: socketPair },
    });
  };

  // REST snapshot so the book shows up before the first stream message
  const loadSnapshot = async () => {
    try {
      const response = await axios.get(
        `https://fapi.binance.com/fapi/v1/depth?symbol=${socketPair}&limit=${Math.max(limit, 20)}`,
      );
      if (!isClosed && !latestDepth) dispatchOrderBook(response.data, "rest");
    } catch (e) {
      // The stream fills the book anyway
    }
  };

  const flushDepth = () => {
    depthFlushTimeout = null;
    if (isClosed || !latestDepth) return;
    dispatchOrderBook(latestDepth, "stream");
  };

  // The header shows one pair: drop what arrives for another one
  const isCurrentPair = () =>
    getState().futures.pair.slug.toLowerCase() === socketPair;

  // The mark price is the one price of the page: the form, the header, the chart and the
  // server's entry price all follow it, so a position opens at the price the user saw
  const handleMarkPrice = (markPrice) => {
    if (!isCurrentPair()) return;
    const p = markPrice?.p;
    if (p == null) return;
    dispatch({ type: FUTURES.CHANGE_MARK_PRICE, payload: p });
    dispatch({ type: FUTURES.UPDATE_PRICE, payload: p });
    const eventTime = Number(markPrice?.E);
    if (Number.isFinite(eventTime)) {
      dispatch({ type: FUTURES.SET_MARK_TICK_AT, payload: eventTime });
    }
  };

  const handleTicker = (tick) => {
    if (!tick || tick.c == null || !isCurrentPair()) return;

    const state = getState();
    // 24h statistics only: the last trade price is not the price positions open at
    dispatch({
      type: FUTURES.CHANGE_TICKER_DATA,
      payload: {
        ...state?.futures?.tickerData,
        ...toTickerData(tick),
      },
    });
  };

  // Partial depth stream: every message is the whole top of the book, not a diff,
  // so its update ids do not follow each other and there is nothing to resync
  const handleDepth = (depth) => {
    if (!Array.isArray(depth?.a) || !Array.isArray(depth?.b)) return;

    latestDepth = { asks: depth.a, bids: depth.b, lastUpdateId: depth.u };
    if (!depthFlushTimeout) {
      depthFlushTimeout = setTimeout(flushDepth, ORDER_BOOK_UPDATE_DELAY);
    }
  };

  loadSnapshot();

  const unsubscribers = [
    subscribeBinanceStream(BINANCE_STREAM_HOST.MARKET, `${socketPair}@markPrice@1s`, handleMarkPrice),
    subscribeBinanceStream(BINANCE_STREAM_HOST.MARKET, `${socketPair}@ticker`, handleTicker),
    subscribeBinanceStream(BINANCE_STREAM_HOST.PUBLIC, `${socketPair}@${depthStream}`, handleDepth),
  ];

  return () => {
    isClosed = true;
    clearTimeout(depthFlushTimeout);
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
};

export const getOrderBookData = (symbol, limit = 50) => async (dispatch) => {
  try {
    const currentPair = symbol ? symbol.toLowerCase() : store.getState().futures.pair.slug.toLowerCase();

    dispatch({ type: FUTURES.FUTURE_ORDER_BOOK_LOADING, payload: true });

    const response = await axios.get(
      `https://fapi.binance.com/fapi/v1/depth?symbol=${currentPair}&limit=${limit}`,
    );
    console.log("Order Book Data:", response.data);
    dispatch({
      type: FUTURES.UPDATE_ORDER_BOOK,
      payload: response.data,
      meta: { source: "rest", pair: currentPair },
    });
    dispatch({ type: FUTURES.FUTURE_ORDER_BOOK_LOADING, payload: false });

  } catch (error) {
    console.error("Error fetching order book:", error);
    dispatch({ type: FUTURES.FUTURE_ORDER_BOOK_LOADING, payload: false });
  }
};

export const getTickerData = () => async (dispatch, getState) => {
  try {
    const state = getState();

    const currentPair = state.futures.pair.slug;
    const normalizedPair = currentPair ? currentPair.toLowerCase() : "null";

    const tickersMap = state.futures.tickersMap;

    if (tickersMap && tickersMap[normalizedPair]) {
      const cachedData = tickersMap[normalizedPair];

      dispatch({
        type: FUTURES.CHANGE_TICKER_DATA,
        payload: {
          lastPrice: cachedData.lastPrice,
          priceChangePercent: cachedData.priceChangePercent,
          highPrice: cachedData.highPrice,
          lowPrice: cachedData.lowPrice,
          volume: cachedData.volume,
          quoteVolume: cachedData.quoteVolume,
        },
      });

      dispatch({ type: FUTURES.FUTURE_TICKERS_LOADING, payload: false });
    } else {
      console.warn(
        `⚠️ CACHE MISS! Data not found for "${normalizedPair}". Going to network...`,
      );
      dispatch({ type: FUTURES.FUTURE_TICKERS_LOADING, payload: true });
    }

    const tickerPromise = axios.get(
      `https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${normalizedPair}`,
    );
    const markPricePromise = axios.get(
      `https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${normalizedPair}`,
    );

    const tickerRes = await tickerPromise;
    const tickerData = tickerRes.data;

    dispatch({
      type: FUTURES.CHANGE_TICKER_DATA,
      payload: {
        lastPrice: tickerData.lastPrice,
        priceChangePercent: tickerData.priceChangePercent,
        highPrice: tickerData.highPrice,
        lowPrice: tickerData.lowPrice,
        volume: tickerData.volume,
        quoteVolume: tickerData.quoteVolume,
      },
    });

    dispatch({ type: FUTURES.FUTURE_TICKERS_LOADING, payload: false });

    const markPriceRes = await markPricePromise;

    dispatch({
      type: FUTURES.CHANGE_MARK_PRICE,
      payload: markPriceRes.data.markPrice,
    });

    dispatch({
      type: FUTURES.UPDATE_PRICE,
      payload: markPriceRes.data.markPrice,
    });

    const markTime = Number(markPriceRes.data.time);
    if (Number.isFinite(markTime)) {
      dispatch({ type: FUTURES.SET_MARK_TICK_AT, payload: markTime });
    }
  } catch (error) {
    console.error(`❌ Error:`, error);
    dispatch({ type: FUTURES.FUTURE_TICKERS_LOADING, payload: false });
  } finally {
    console.groupEnd();
  }
};

export const getAllTickers = () => async (dispatch) => {
  try {
    const response = await axios.get(
      "https://fapi.binance.com/fapi/v1/ticker/24hr",
    );

    const tickersMap = response.data.reduce((acc, item) => {
      acc[item.symbol.toLowerCase()] = {
        lastPrice: item.lastPrice,
        priceChangePercent: item.priceChangePercent,
        highPrice: item.highPrice,
        lowPrice: item.lowPrice,
        volume: item.volume,
        quoteVolume: item.quoteVolume,
      };
      return acc;
    }, {});

    dispatch({
      type: FUTURES.UPDATE_ALL_TICKERS_CACHE,
      payload: tickersMap,
    });
  } catch (error) {
    console.error("Error fetching all tickers:", error);
  }
};

/**
 * Live tickers of the given pairs, merged into the tickers cache.
 * @param {string[]} symbols e.g. ["BTCUSDT"]
 * @returns {() => void} unsubscribe
 */
export const subscribeTickers = (symbols) => (dispatch, getState) => {
  let pendingTickers = {};
  let flushTimeout = null;

  const flush = () => {
    flushTimeout = null;
    const updates = pendingTickers;
    pendingTickers = {};

    dispatch({
      type: FUTURES.UPDATE_ALL_TICKERS_CACHE,
      payload: { ...getState().futures.tickersMap, ...updates },
    });
  };

  const unsubscribers = symbols.map((symbol) => {
    const symbolLower = symbol.toLowerCase();

    return subscribeBinanceStream(
      BINANCE_STREAM_HOST.MARKET,
      `${symbolLower}@ticker`,
      (tick) => {
        if (tick?.c == null) return;

        pendingTickers[symbolLower] = toTickerData(tick);
        if (!flushTimeout) {
          flushTimeout = setTimeout(flush, TICKERS_FLUSH_DELAY_MS);
        }
      },
    );
  });

  return () => {
    clearTimeout(flushTimeout);
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
};
